import React, { useState, useCallback } from 'react';
import { Upload, X, ImageIcon, Loader2, AlertCircle, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function PhotoUpload({ itemId, currentPhotos = [], onUploadSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploadMethod, setUploadMethod] = useState('file'); // 'file' or 'url'

  const MAX_PHOTOS = 5;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `Invalid file type. Allowed: JPG, PNG, WEBP`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size: 5MB`;
    }
    if (currentPhotos.length >= MAX_PHOTOS) {
      return `Maximum ${MAX_PHOTOS} photos allowed per item`;
    }
    return null;
  };

  const validateUrl = (url) => {
    if (!url || !url.trim()) {
      return 'Please enter a photo URL';
    }
    
    // Basic URL validation
    try {
      new URL(url);
    } catch (e) {
      return 'Please enter a valid URL';
    }

    // Check if it looks like an image URL
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
    const hasImageExtension = imageExtensions.some(ext => url.toLowerCase().includes(ext));
    const hasImageHost = url.includes('unsplash') || url.includes('pexels') || url.includes('imgur') || url.includes('cloudinary');
    
    if (!hasImageExtension && !hasImageHost) {
      return 'URL does not appear to be an image. Make sure it ends with .jpg, .png, or .webp';
    }

    if (currentPhotos.length >= MAX_PHOTOS) {
      return `Maximum ${MAX_PHOTOS} photos allowed per item`;
    }

    return null;
  };

  const uploadPhotoFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/vendor/menu/${itemId}/photos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to upload photo');
    }
  };

  const addPhotoByUrl = async (url) => {
    try {
      const response = await api.post(`/vendor/menu/${itemId}/photos/url`, {
        url: url.trim()
      });

      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to add photo from URL');
    }
  };

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);
    
    if (fileArray.length === 0) return;

    // Validate first file
    const file = fileArray[0]; // Only handle one file at a time for now
    const validationError = validateFile(file);
    
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const photoData = await uploadPhotoFile(file);
      toast.success('Photo uploaded successfully!');
      
      if (onUploadSuccess) {
        onUploadSuccess(photoData);
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = async () => {
    const validationError = validateUrl(photoUrl);
    
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const photoData = await addPhotoByUrl(photoUrl);
      toast.success('Photo added from URL successfully!');
      setPhotoUrl('');
      
      if (onUploadSuccess) {
        onUploadSuccess(photoData);
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [itemId, currentPhotos]);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await api.delete(`/vendor/menu/${itemId}/photos/${photoId}`);
      toast.success('Photo deleted');
      
      if (onUploadSuccess) {
        onUploadSuccess(null); // Trigger refresh
      }
    } catch (err) {
      toast.error('Failed to delete photo');
    }
  };

  const remainingSlots = MAX_PHOTOS - currentPhotos.length;

  return (
    <div className="space-y-4">
      {/* Photo Gallery */}
      {currentPhotos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {currentPhotos.map((photo, index) => (
            <div
              key={photo.photo_id || index}
              className="relative group aspect-square rounded-lg overflow-hidden border-2 border-border bg-muted"
              data-testid={`photo-${photo.photo_id}`}
            >
              <img
                src={photo.url}
                alt={`Product ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = '/placeholder-image.png';
                  e.target.classList.add('opacity-50');
                }}
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDeletePhoto(photo.photo_id)}
                  data-testid={`delete-photo-${photo.photo_id}`}
                >
                  <X className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
              {index === 0 && (
                <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                  Primary
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Area with Tabs */}
      {remainingSlots > 0 && (
        <Tabs value={uploadMethod} onValueChange={setUploadMethod} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="file" data-testid="upload-tab-file">
              <Upload className="h-4 w-4 mr-2" />
              Upload File
            </TabsTrigger>
            <TabsTrigger value="url" data-testid="upload-tab-url">
              <LinkIcon className="h-4 w-4 mr-2" />
              Add by URL
            </TabsTrigger>
          </TabsList>

          {/* File Upload Tab */}
          <TabsContent value="file" className="mt-4">
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-primary bg-primary/5' 
                  : 'border-border hover:border-primary/50'
              } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="photo-upload-dropzone"
            >
              <input
                type="file"
                id="photo-upload-input"
                className="hidden"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileInput}
                disabled={uploading}
              />
              
              <div className="flex flex-col items-center gap-3">
                {uploading ? (
                  <>
                    <Loader2 className="h-10 w-10 text-primary animate-spin" />
                    <p className="text-sm font-medium">Uploading photo...</p>
                  </>
                ) : (
                  <>
                    <div className="p-3 rounded-full bg-muted">
                      <Upload className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">
                        {dragActive ? 'Drop photo here' : 'Drag & drop or click to upload'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        JPG, PNG, WEBP • Max 5MB • {remainingSlots} slot{remainingSlots !== 1 ? 's' : ''} remaining
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('photo-upload-input').click()}
                      data-testid="photo-upload-button"
                    >
                      <ImageIcon className="h-4 w-4 mr-2" />
                      Choose Photo
                    </Button>
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {/* URL Upload Tab */}
          <TabsContent value="url" className="mt-4">
            <div className="border-2 border-dashed rounded-lg p-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="photo-url-input" className="text-sm font-medium">
                    Photo URL
                  </Label>
                  <div className="flex gap-2 mt-2">
                    <div className="relative flex-1">
                      <LinkIcon className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="photo-url-input"
                        type="url"
                        placeholder="https://example.com/photo.jpg"
                        value={photoUrl}
                        onChange={(e) => setPhotoUrl(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && photoUrl.trim()) {
                            handleUrlSubmit();
                          }
                        }}
                        disabled={uploading}
                        className="pl-10"
                        data-testid="photo-url-input"
                      />
                    </div>
                    <Button
                      onClick={handleUrlSubmit}
                      disabled={uploading || !photoUrl.trim()}
                      data-testid="add-url-button"
                    >
                      {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Add Photo'
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    💡 Tip: You can use images from Unsplash, Pexels, or any direct image URL
                  </p>
                </div>

                <div className="bg-muted/50 rounded-lg p-3 text-xs space-y-1">
                  <p className="font-medium">Supported sources:</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                    <li>Direct image URLs ending in .jpg, .png, .webp</li>
                    <li>Unsplash.com images</li>
                    <li>Pexels.com images</li>
                    <li>Cloudinary, Imgur, and other image hosts</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Info */}
      {currentPhotos.length === 0 && (
        <p className="text-xs text-muted-foreground text-center">
          📸 Tip: First photo will be used as the primary image in listings
        </p>
      )}
    </div>
  );
}
